/**
 * Application entry point that initializes the dependency container and renders the root App component.
 * Sets up React context providers for app state, theme, and focus navigation dependencies.
 */
import React from 'react'
import * as ReactJsxRuntime from 'react/jsx-runtime'
import ReactDOM from 'react-dom/client'
import * as BgioCore from 'boardgame.io/core'
import * as BgioAi from 'boardgame.io/ai'
import * as BgioInternal from 'boardgame.io/internal'
import App from './App.tsx'
import './index.css'
import { ThemeProvider } from './contexts/ThemeContext'
import { FocusNavigationProvider } from './components/providers/FocusNavigationProvider/FocusNavigationProvider'
import { DepProvider } from './components/providers/DepProvider/DepProvider'
import { makeWebContainer } from './deps/containers/container'
import { DynamicGamePilot } from './devTools/DynamicGamePilot'
import { AdminApp } from './AdminApp.tsx'

// Exposes this app's own React instance so dynamically import()ed game
// bundles (see deps/catalog/dynamicGameLoader) resolve "react"/
// "react/jsx-runtime" to the exact same module via the import map in
// index.html + public/vendor/*.mjs, instead of loading a duplicate copy.
;(window as unknown as { __MERELS_SHARED_DEPS__: unknown }).__MERELS_SHARED_DEPS__ = {
  react: React,
  reactJsxRuntime: ReactJsxRuntime,
  bgioCore: BgioCore,
  bgioAi: BgioAi,
  bgioInternal: BgioInternal,
}

const isDev = import.meta.env.MODE === 'development' || window.location.hostname === 'localhost'
const isWorktreeRouter = window.location.hostname.endsWith('.localhost')

// Worker URL: Use VITE_WORKER_URL if set, otherwise:
// - If accessed via worktree router (*.localhost), use same origin (router forwards /api/* to worker)
// - In dev mode, default to localhost:8787
// - In production, use deployed worker
const baseUrl = import.meta.env.VITE_WORKER_URL || (isWorktreeRouter
  ? window.location.origin
  : isDev
    ? 'http://localhost:8787'
    : 'https://merels-worker.yuriplex.workers.dev')

const container = makeWebContainer(baseUrl)

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  container.roomLink.disconnect()
})

// Manual verification harness for the dynamic-game-loading pilot (see
// deps/catalog/dynamicGameLoader). Not part of any production flow.
// Admin site portal routing
const isDynamicGamePilot = window.location.pathname === '/dev/dynamic-game-pilot'
const isAdminSite = window.location.pathname.startsWith('/admin')
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {isDynamicGamePilot ? (
      <DynamicGamePilot workerUrl={baseUrl} />
    ) : isAdminSite ? (
      <ThemeProvider>
        <DepProvider container={container}>
          <AdminApp />
        </DepProvider>
      </ThemeProvider>
    ) : (
      <ThemeProvider>
        <FocusNavigationProvider
          focusNavigation={container.focusNavigation}
          controllerInput={container.controllerInput}
        >
          <DepProvider container={container}>
            <App container={container} />
          </DepProvider>
        </FocusNavigationProvider>
      </ThemeProvider>
    )}
  </React.StrictMode>,
)
